/**
 * sqlite3mt — SAB (SharedArrayBuffer) layout definitions
 *
 * Mechanical derivation from §96 §4.1–§4.4.
 *
 * This module defines byte offsets and buffer-sizing functions for all four
 * SAB regions. Every inter-worker data path goes through these layouts.
 *
 * Layout convention:
 *   - SAB4 per-connection arrays come first (easy to zero on bootstrap).
 *   - SAB4 scalar control slots follow the per-connection region.
 *   - All Int32 slots are accessed via Atomics on Int32Array views.
 *   - All BigInt64 slots are accessed via Atomics on BigInt64Array views.
 */

// ── Configuration ───────────────────────────────────────────────

/** Maximum concurrent SQLite connections per device (§4.4). */
export const MAX_CONN = 8

/**
 * Number of page-cache slots in SAB1.
 * Must be >= typical working set for effective read caching.
 */
export const NUM_SLOTS = 64

/** Default SQLite page size. */
export const DEFAULT_PAGE_SIZE = 4096

/**
 * Maximum WAL shadow size in bytes (SAB2).
 *
 * SAB2 must hold the WAL bytes for one SQLite commit while the client worker
 * extracts the committed page delta. Product workloads can legitimately write
 * large JSON/blob rows plus FTS/index updates in one transaction. The default
 * max must therefore be an application-grade commit budget while still fitting
 * the wal-index/SHM backing for the configured page size. The initial SAB2
 * allocation is smaller and grows synchronously up to this max when the JS
 * runtime supports growable SharedArrayBuffer. Tests can still force low hard
 * limits through `useSqlite3Mt.maxWalSize`.
 */
export const MAX_WAL_SHADOW_SIZE = 128 * 1024 * 1024
export const DEFAULT_INITIAL_WAL_SHADOW_SIZE = 32 * 1024 * 1024

const WAL_HEADER_BYTES = 32
const WAL_FRAME_HEADER_BYTES = 24
const WAL_INDEX_HEADER_BYTES = 136
const WAL_INDEX_FRAMES_PER_SHM_REGION = 4096

/**
 * SQLite wal-index SHM region size. SQLite maps the WAL index in 32 KiB
 * regions; region 0 contains the headers/checkpoint block and later regions
 * extend the hash/index area as the WAL grows.
 */
export const SHM_DATA_SIZE = 32768

/**
 * Number of SQLite wal-index SHM regions backed by SAB3.
 *
 * Browser connections still default to a 128 MiB WAL shadow budget. The extra
 * SHM backing lets bounded component jobs opt into a larger single-transaction
 * budget without changing browser SAB2 defaults.
 */
export const SHM_REGION_COUNT = 16

/** Total WAL-index bytes backed by SAB3, excluding the lock region. */
export const SAB3_SHM_DATA_SIZE = SHM_DATA_SIZE * SHM_REGION_COUNT

/**
 * SAB3 lock region: 8 × Int32 (§4.3).
 * Indices: WRITE=0, CKPT=1, RECOVER=2, READ(0..4)=3..7
 */
export const SHM_LOCK_COUNT = 8
export const SHM_LOCK_SIZE_BYTES = SHM_LOCK_COUNT * 4

// ── SAB3 lock indices (§4.3) ────────────────────────────────────

export const SHM_LOCK_WRITE = 0
export const SHM_LOCK_CKPT = 1
export const SHM_LOCK_RECOVER = 2
// SHM_LOCK_READ(n) = 3 + n, for n in 0..4

export function shmLockRead(n: number): number {
  return 3 + n
}

// ── SAB1 — MainDb slot-window (§4.1) ────────────────────────────

/**
 * SAB1 control area: two Int32 slots at the start.
 *   PAGE_TABLE_LOCK : Int32  — mutex for page table mutations
 *   DB_PAGE_COUNT   : Int32  — effective page count for DB-view
 */
export const SAB1_CTRL_INTS = 2
export const SAB1_CTRL_BYTES = SAB1_CTRL_INTS * 4

export const SAB1_PAGE_TABLE_LOCK_I32 = 0
export const SAB1_DB_PAGE_COUNT_I32 = 1

/**
 * Per-slot fields in SAB1 (all in bytes relative to slot base).
 *   SLOT_LOCK    : Int32
 *   SLOT_STATE   : Int32
 *   SLOT_PGNO    : Int32
 *   SLOT_GEN     : BigInt64  (8 bytes, 4-byte aligned → offset 12)
 *   SLOT_WAITSEQ : Int32
 *   SLOT_DATA    : Uint8[pageSize]
 */
export const SLOT_LOCK_OFF = 0       // Int32
export const SLOT_STATE_OFF = 4      // Int32
export const SLOT_PGNO_OFF = 8       // Int32
export const SLOT_GEN_OFF = 12       // BigInt64
export const SLOT_WAITSEQ_OFF = 20   // Int32
export const SLOT_DATA_OFF = 24      // Uint8[pageSize]

/** Size of a single slot's metadata (before page data). */
export const SLOT_META_BYTES = 24

/**
 * Int32 indices within sab1SlotMetaView().
 * SLOT_GEN is accessed separately through sab1LoadSlotGen/sab1StoreSlotGen().
 */
export const enum SlotMetaI32 {
  LOCK = SLOT_LOCK_OFF / 4,
  STATE = SLOT_STATE_OFF / 4,
  PGNO = SLOT_PGNO_OFF / 4,
  WAITSEQ = SLOT_WAITSEQ_OFF / 4,
}

/**
 * Compute per-slot stride in bytes.
 * Padded so that page data starts at a 4-byte-aligned boundary.
 */
export function slotStride(pageSize: number): number {
  return SLOT_META_BYTES + pageSize
}

/**
 * Compute total SAB1 byte size.
 */
export function sab1ByteSize(pageSize: number = DEFAULT_PAGE_SIZE): number {
  return SAB1_CTRL_BYTES + NUM_SLOTS * slotStride(pageSize)
}

/**
 * Get the byte offset of slot i's base in SAB1.
 */
export function sab1SlotBase(i: number, pageSize: number = DEFAULT_PAGE_SIZE): number {
  return SAB1_CTRL_BYTES + i * slotStride(pageSize)
}

// ── SAB2 — WAL-bytes shadow (§4.2) ──────────────────────────────

/**
 * SAB2 layout:
 *   WAL_DATA_SIZE : Int32  (byte 0–3) — current valid prefix length
 *   WAL_DATA      : bytes  (byte 4+)  — WAL file content shadow
 */
export const SAB2_SIZE_OFF = 0   // Int32 at byte 0
export const SAB2_DATA_OFF = 4   // WAL bytes start

export function sab2ByteSize(maxWalSize: number = MAX_WAL_SHADOW_SIZE): number {
  return SAB2_DATA_OFF + maxWalSize
}

// ── SAB3 — SHM + lock region (§4.3) ─────────────────────────────

/**
 * SAB3 layout:
 *   Bytes 0..SAB3_SHM_DATA_SIZE-1                    : wal-index data
 *   Bytes SAB3_SHM_DATA_SIZE..SAB3_SHM_DATA_SIZE+31  : lock region (8 × Int32)
 */
export const SAB3_SHM_OFF = 0
export const SAB3_LOCK_OFF = SAB3_SHM_DATA_SIZE

export function sab3ByteSize(): number {
  return SAB3_SHM_DATA_SIZE + SHM_LOCK_SIZE_BYTES
}

// ── SAB4 — ACK, frontier, gates (§4.4) ──────────────────────────
//
// Layout (byte offsets):
//
// Per-connection arrays (indexed by connId):
//   COMMIT_POST_SEQ[connId]  : BigInt64  — max reqId posted by Client Worker
//   COMMIT_ACK_SEQ[connId]   : BigInt64  — max reqId ACK'd by Control Worker
//   COMMIT_ACK_RC[connId]    : Int32     — 0=OK, 1=FATAL
//
// Scalar control slots:
//   CONTROL_RUNTIME_EPOCH    : BigInt64  — unique generation id
//   CONTROL_READY            : Int32     — 0=bootstrap, 1=normal
//   CONTROL_READY_WAITSEQ    : Int32
//   FRONTIER_SEQ             : Int32
//   FRONTIER_VALID           : Int32
//   FRONTIER_SALT0           : Int32
//   FRONTIER_SALT1           : Int32
//   FRONTIER_INTEGRATED_MAX  : Int32
//   FRONTIER_WAITSEQ         : Int32
//   FATAL_FLAG               : Int32     — 0=normal, 1=fatal
//   WRITE_GATE_CLOSED        : Int32     — 0=open, 1=closed
//   WRITE_GATE_REASON        : Int32     — enum, informative
//   WRITE_GATE_WAITSEQ       : Int32
//   CHECKPOINT_GATE_CLOSED   : Int32     — 0=open, 1=no new writes
//   CHECKPOINT_GATE_WAITSEQ  : Int32
//   WAL_WRITE_HOLDERS        : Int32     — count of connections holding WAL_WRITE
//   MAIN_DB_READER_COUNT     : Int32     — shared-lock holders on the main DB
//   MAIN_DB_WRITER_CONN      : Int32     — reserved/pending/exclusive owner (connId+1), 0=none
//   WAL_INDEX_RESET_SEQ      : Int32     — bumped when WAL/SHM is externally reset
//

// Per-connection array byte offsets (base of SAB4 buffer)
export const SAB4_COMMIT_POST_SEQ_OFF = 0                                          // MAX_CONN × BigInt64
export const SAB4_COMMIT_ACK_SEQ_OFF = SAB4_COMMIT_POST_SEQ_OFF + MAX_CONN * 8     // MAX_CONN × BigInt64
export const SAB4_COMMIT_ACK_RC_OFF = SAB4_COMMIT_ACK_SEQ_OFF + MAX_CONN * 8       // MAX_CONN × Int32

/** Byte offset where scalar slots begin (after per-connection arrays). */
export const SAB4_SCALAR_BASE = SAB4_COMMIT_ACK_RC_OFF + MAX_CONN * 4

// Scalar slot byte offsets (relative to SAB4 buffer start)
// Each is at SAB4_SCALAR_BASE + a fixed offset; we lay them out sequentially.
// BigInt64 slots must be 8-byte aligned.

/** Byte offset of CONTROL_RUNTIME_EPOCH (BigInt64, 8 bytes). */
export const SAB4_RUNTIME_EPOCH_OFF = SAB4_SCALAR_BASE

/** Byte offset after the epoch — start of Int32 scalar block. */
export const SAB4_I32_SCALAR_BASE = SAB4_RUNTIME_EPOCH_OFF + 8

// Int32 scalar slots (sequential from SAB4_I32_SCALAR_BASE)
export const SAB4_CONTROL_READY_OFF = SAB4_I32_SCALAR_BASE + 0
export const SAB4_CONTROL_READY_WAITSEQ_OFF = SAB4_I32_SCALAR_BASE + 4
export const SAB4_FRONTIER_SEQ_OFF = SAB4_I32_SCALAR_BASE + 8
export const SAB4_FRONTIER_VALID_OFF = SAB4_I32_SCALAR_BASE + 12
export const SAB4_FRONTIER_SALT0_OFF = SAB4_I32_SCALAR_BASE + 16
export const SAB4_FRONTIER_SALT1_OFF = SAB4_I32_SCALAR_BASE + 20
export const SAB4_FRONTIER_INTEGRATED_MAX_OFF = SAB4_I32_SCALAR_BASE + 24
export const SAB4_FRONTIER_WAITSEQ_OFF = SAB4_I32_SCALAR_BASE + 28
export const SAB4_FATAL_FLAG_OFF = SAB4_I32_SCALAR_BASE + 32
export const SAB4_WRITE_GATE_CLOSED_OFF = SAB4_I32_SCALAR_BASE + 36
export const SAB4_WRITE_GATE_REASON_OFF = SAB4_I32_SCALAR_BASE + 40
export const SAB4_WRITE_GATE_WAITSEQ_OFF = SAB4_I32_SCALAR_BASE + 44
export const SAB4_CHECKPOINT_GATE_CLOSED_OFF = SAB4_I32_SCALAR_BASE + 48
export const SAB4_CHECKPOINT_GATE_WAITSEQ_OFF = SAB4_I32_SCALAR_BASE + 52
export const SAB4_WAL_WRITE_HOLDERS_OFF = SAB4_I32_SCALAR_BASE + 56
export const SAB4_MAIN_DB_READER_COUNT_OFF = SAB4_I32_SCALAR_BASE + 60
export const SAB4_MAIN_DB_WRITER_CONN_OFF = SAB4_I32_SCALAR_BASE + 64
export const SAB4_WAL_INDEX_RESET_SEQ_OFF = SAB4_I32_SCALAR_BASE + 68

/** Total bytes used by scalar Int32 region. */
export const SAB4_I32_SCALAR_BYTES = 72 // 18 × Int32

/** Total SAB4 byte size. */
export function sab4ByteSize(): number {
  return SAB4_I32_SCALAR_BASE + SAB4_I32_SCALAR_BYTES
}

// ── SAB4 typed-view helpers ─────────────────────────────────────

/**
 * Per-connection slot access: byte offset for COMMIT_POST_SEQ[connId].
 * (BigInt64 — use BigInt64Array view)
 */
export function sab4CommitPostSeqOff(connId: number): number {
  return SAB4_COMMIT_POST_SEQ_OFF + connId * 8
}

/**
 * Per-connection slot access: byte offset for COMMIT_ACK_SEQ[connId].
 * (BigInt64 — use BigInt64Array view)
 */
export function sab4CommitAckSeqOff(connId: number): number {
  return SAB4_COMMIT_ACK_SEQ_OFF + connId * 8
}

/**
 * Per-connection slot access: byte offset for COMMIT_ACK_RC[connId].
 * (Int32 — use Int32Array view)
 */
export function sab4CommitAckRcOff(connId: number): number {
  return SAB4_COMMIT_ACK_RC_OFF + connId * 4
}

// ── SAB allocation helper ───────────────────────────────────────

/**
 * Configuration for allocating the full SAB set.
 */
export interface SabConfig {
  pageSize?: number
  initialWalSize?: number
  maxWalSize?: number
}

/**
 * Computed byte sizes for all four SAB regions.
 */
export interface SabSizes {
  sab1: number
  sab2: number
  sab2Max: number
  sab3: number
  sab4: number
}

export interface WalShadowSizeConfig {
  initialWalSize: number
  maxWalSize: number
}

export function normalizeWalShadowSizeConfig(config: SabConfig = {}): WalShadowSizeConfig {
  const pageSize = config.pageSize ?? DEFAULT_PAGE_SIZE
  const shmBackedMaxWalSize = maxWalShadowSizeForPageSize(pageSize)
  const maxWalSize = config.maxWalSize ?? Math.min(MAX_WAL_SHADOW_SIZE, shmBackedMaxWalSize)
  if (!Number.isInteger(maxWalSize) || maxWalSize <= 0) {
    throw new RangeError('sqlite3mt maxWalSize must be a positive integer')
  }
  if (maxWalSize > shmBackedMaxWalSize) {
    throw new RangeError('sqlite3mt maxWalSize exceeds the configured wal-index SHM backing capacity')
  }

  const defaultInitialWalSize = Math.min(DEFAULT_INITIAL_WAL_SHADOW_SIZE, maxWalSize)
  const requestedInitialWalSize = config.initialWalSize ?? defaultInitialWalSize
  if (!Number.isInteger(requestedInitialWalSize) || requestedInitialWalSize <= 0) {
    throw new RangeError('sqlite3mt initialWalSize must be a positive integer')
  }
  if (config.initialWalSize != null && requestedInitialWalSize > maxWalSize) {
    throw new RangeError('sqlite3mt initialWalSize must not exceed maxWalSize')
  }

  return {
    initialWalSize: requestedInitialWalSize,
    maxWalSize,
  }
}

export function maxWalShadowSizeForPageSize(pageSize: number = DEFAULT_PAGE_SIZE): number {
  if (!Number.isInteger(pageSize) || pageSize <= 0) {
    throw new RangeError('sqlite3mt pageSize must be a positive integer')
  }

  const firstRegionFrameCapacity = WAL_INDEX_FRAMES_PER_SHM_REGION - (WAL_INDEX_HEADER_BYTES / 4)
  const laterRegionFrameCapacity = Math.max(0, SHM_REGION_COUNT - 1) * WAL_INDEX_FRAMES_PER_SHM_REGION
  const maxFrames = firstRegionFrameCapacity + laterRegionFrameCapacity
  return WAL_HEADER_BYTES + maxFrames * (WAL_FRAME_HEADER_BYTES + pageSize)
}

/**
 * Compute byte sizes for all SAB regions.
 */
export function computeSabSizes(config: SabConfig = {}): SabSizes {
  const pageSize = config.pageSize ?? DEFAULT_PAGE_SIZE
  const { initialWalSize, maxWalSize } = normalizeWalShadowSizeConfig(config)
  return {
    sab1: sab1ByteSize(pageSize),
    sab2: sab2ByteSize(initialWalSize),
    sab2Max: sab2ByteSize(maxWalSize),
    sab3: sab3ByteSize(),
    sab4: sab4ByteSize(),
  }
}

function allocateMaybeGrowableSharedArrayBuffer(
  initialByteLength: number,
  maxByteLength: number,
): SharedArrayBuffer {
  if (maxByteLength > initialByteLength) {
    try {
      const ctor = SharedArrayBuffer as unknown as {
        new(byteLength: number, options?: { maxByteLength?: number }): SharedArrayBuffer
      }
      const buffer = new ctor(initialByteLength, { maxByteLength })
      const maybe = buffer as SharedArrayBuffer & { growable?: boolean; maxByteLength?: number }
      if (maybe.growable === true && maybe.maxByteLength === maxByteLength) {
        return buffer
      }
    } catch {
      // Older JS runtimes do not support growable SharedArrayBuffer yet.
    }
  }

  return new SharedArrayBuffer(maxByteLength)
}

/**
 * Allocate all four SAB regions as SharedArrayBuffers.
 * Returns raw buffers — callers create typed views as needed.
 */
export function allocateSabSet(config: SabConfig = {}): {
  sab1: SharedArrayBuffer
  sab2: SharedArrayBuffer
  sab3: SharedArrayBuffer
  sab4: SharedArrayBuffer
} {
  const sizes = computeSabSizes(config)
  return {
    sab1: new SharedArrayBuffer(sizes.sab1),
    sab2: allocateMaybeGrowableSharedArrayBuffer(sizes.sab2, sizes.sab2Max),
    sab3: new SharedArrayBuffer(sizes.sab3),
    sab4: new SharedArrayBuffer(sizes.sab4),
  }
}

export function sab2MaxByteLength(sab2: SharedArrayBuffer): number {
  const maybe = sab2 as SharedArrayBuffer & { maxByteLength?: number }
  return maybe.maxByteLength ?? sab2.byteLength
}

export function sab2MaxWalByteLength(sab2: SharedArrayBuffer): number {
  return sab2MaxByteLength(sab2) - SAB2_DATA_OFF
}

export function ensureSab2WalCapacity(sab2: SharedArrayBuffer, requiredWalBytes: number): boolean {
  if (!Number.isInteger(requiredWalBytes) || requiredWalBytes < 0) {
    return false
  }

  const requiredByteLength = SAB2_DATA_OFF + requiredWalBytes
  if (requiredByteLength <= sab2.byteLength) {
    return true
  }

  const maxByteLength = sab2MaxByteLength(sab2)
  if (requiredByteLength > maxByteLength) {
    return false
  }

  const maybe = sab2 as SharedArrayBuffer & {
    growable?: boolean
    grow?: (newLength: number) => void
  }
  if (maybe.growable !== true || typeof maybe.grow !== 'function') {
    return false
  }

  const doubled = Math.max(requiredByteLength, sab2.byteLength * 2)
  const nextByteLength = Math.min(maxByteLength, Math.ceil(doubled / 4096) * 4096)
  maybe.grow(nextByteLength)
  return sab2.byteLength >= requiredByteLength
}

// ── SAB1 typed views ────────────────────────────────────────────

/**
 * Create the Int32Array view over SAB1 control area.
 * Indices: SAB1_PAGE_TABLE_LOCK_I32, SAB1_DB_PAGE_COUNT_I32.
 */
export function sab1CtrlView(sab1: SharedArrayBuffer): Int32Array {
  return new Int32Array(sab1, 0, SAB1_CTRL_INTS)
}

/**
 * Create Int32Array view for slot i's metadata.
 * Layout: LOCK(0), STATE(1), PGNO(2), GEN_LO(3), GEN_HI(4), WAITSEQ(5)
 * Note: GEN lives at byte offset 12 and is accessed via DataView helpers.
 */
export function sab1SlotMetaView(
  sab1: SharedArrayBuffer,
  i: number,
  pageSize: number = DEFAULT_PAGE_SIZE
): Int32Array {
  const base = sab1SlotBase(i, pageSize)
  return new Int32Array(sab1, base, SLOT_META_BYTES / 4)
}

/**
 * SLOT_GEN is intentionally stored at byte offset 12 per spec.
 * That offset is not 8-byte aligned, so BigInt64Array cannot view it directly.
 */
function sab1SlotGenDataView(
  sab1: SharedArrayBuffer,
  i: number,
  pageSize: number = DEFAULT_PAGE_SIZE
): DataView {
  const base = sab1SlotBase(i, pageSize) + SLOT_GEN_OFF
  return new DataView(sab1, base, 8)
}

export function sab1LoadSlotGen(
  sab1: SharedArrayBuffer,
  i: number,
  pageSize: number = DEFAULT_PAGE_SIZE
): bigint {
  return sab1SlotGenDataView(sab1, i, pageSize).getBigInt64(0, true)
}

export function sab1StoreSlotGen(
  sab1: SharedArrayBuffer,
  i: number,
  generation: bigint,
  pageSize: number = DEFAULT_PAGE_SIZE
): void {
  sab1SlotGenDataView(sab1, i, pageSize).setBigInt64(0, generation, true)
}

/**
 * Create Uint8Array view for slot i's page data buffer.
 */
export function sab1SlotDataView(
  sab1: SharedArrayBuffer,
  i: number,
  pageSize: number = DEFAULT_PAGE_SIZE
): Uint8Array {
  const base = sab1SlotBase(i, pageSize) + SLOT_DATA_OFF
  return new Uint8Array(sab1, base, pageSize)
}

// ── SAB2 typed views ────────────────────────────────────────────

/** View for WAL_DATA_SIZE (single Int32). */
export function sab2SizeView(sab2: SharedArrayBuffer): Int32Array {
  return new Int32Array(sab2, SAB2_SIZE_OFF, 1)
}

/** View for WAL shadow data bytes. */
export function sab2DataView(sab2: SharedArrayBuffer, maxWalSize?: number): Uint8Array {
  return maxWalSize == null
    ? new Uint8Array(sab2, SAB2_DATA_OFF)
    : new Uint8Array(sab2, SAB2_DATA_OFF, maxWalSize)
}

// ── SAB3 typed views ────────────────────────────────────────────

/** View over the wal-index data region. */
export function sab3ShmView(sab3: SharedArrayBuffer): Uint8Array {
  return new Uint8Array(sab3, SAB3_SHM_OFF, SAB3_SHM_DATA_SIZE)
}

/** View over the lock region (8 × Int32). */
export function sab3LockView(sab3: SharedArrayBuffer): Int32Array {
  return new Int32Array(sab3, SAB3_LOCK_OFF, SHM_LOCK_COUNT)
}

// ── SAB4 typed views ────────────────────────────────────────────

/**
 * BigInt64Array view for COMMIT_POST_SEQ[connId].
 * Caller accesses element at index (connId).
 */
export function sab4CommitPostSeqView(sab4: SharedArrayBuffer): BigInt64Array {
  return new BigInt64Array(sab4, SAB4_COMMIT_POST_SEQ_OFF, MAX_CONN)
}

/**
 * BigInt64Array view for COMMIT_ACK_SEQ[connId].
 */
export function sab4CommitAckSeqView(sab4: SharedArrayBuffer): BigInt64Array {
  return new BigInt64Array(sab4, SAB4_COMMIT_ACK_SEQ_OFF, MAX_CONN)
}

/**
 * Int32Array view for COMMIT_ACK_RC[connId].
 */
export function sab4CommitAckRcView(sab4: SharedArrayBuffer): Int32Array {
  return new Int32Array(sab4, SAB4_COMMIT_ACK_RC_OFF, MAX_CONN)
}

/**
 * BigInt64Array view for CONTROL_RUNTIME_EPOCH (single element).
 */
export function sab4RuntimeEpochView(sab4: SharedArrayBuffer): BigInt64Array {
  return new BigInt64Array(sab4, SAB4_RUNTIME_EPOCH_OFF, 1)
}

/**
 * Int32Array view over all scalar Int32 slots in SAB4.
 * Indices use the Sab4Scalar enum.
 */
export function sab4ScalarView(sab4: SharedArrayBuffer): Int32Array {
  return new Int32Array(sab4, SAB4_I32_SCALAR_BASE, SAB4_I32_SCALAR_BYTES / 4)
}

/**
 * Scalar Int32 slot indices within the sab4ScalarView().
 *
 * Each maps to the corresponding byte offset in SAB4.
 * Use: `Atomics.load(sab4ScalarView(sab4), Sab4Scalar.CONTROL_READY)`
 */
export const enum Sab4Scalar {
  CONTROL_READY = 0,
  CONTROL_READY_WAITSEQ = 1,
  FRONTIER_SEQ = 2,
  FRONTIER_VALID = 3,
  FRONTIER_SALT0 = 4,
  FRONTIER_SALT1 = 5,
  FRONTIER_INTEGRATED_MAX = 6,
  FRONTIER_WAITSEQ = 7,
  FATAL_FLAG = 8,
  WRITE_GATE_CLOSED = 9,
  WRITE_GATE_REASON = 10,
  WRITE_GATE_WAITSEQ = 11,
  CHECKPOINT_GATE_CLOSED = 12,
  CHECKPOINT_GATE_WAITSEQ = 13,
  WAL_WRITE_HOLDERS = 14,
  MAIN_DB_READER_COUNT = 15,
  MAIN_DB_WRITER_CONN = 16,
  WAL_INDEX_RESET_SEQ = 17,
}

/**
 * Zero all per-connection arrays in SAB4.
 * Called during bootstrap before CONTROL_READY = 1 (§4.4, R38, R71).
 */
export function sab4ResetConnSlots(sab4: SharedArrayBuffer): void {
  new BigInt64Array(sab4, SAB4_COMMIT_POST_SEQ_OFF, MAX_CONN).fill(0n)
  new BigInt64Array(sab4, SAB4_COMMIT_ACK_SEQ_OFF, MAX_CONN).fill(0n)
  new Int32Array(sab4, SAB4_COMMIT_ACK_RC_OFF, MAX_CONN).fill(0)
  const scalar = sab4ScalarView(sab4)
  Atomics.store(scalar, Sab4Scalar.WAL_WRITE_HOLDERS, 0)
  Atomics.store(scalar, Sab4Scalar.MAIN_DB_READER_COUNT, 0)
  Atomics.store(scalar, Sab4Scalar.MAIN_DB_WRITER_CONN, 0)
}

/**
 * Zero the SAB3 lock region.
 * Called during bootstrap before CONTROL_READY = 1 (§4.3, R72).
 */
export function sab3ResetLocks(sab3: SharedArrayBuffer): void {
  new Int32Array(sab3, SAB3_LOCK_OFF, SHM_LOCK_COUNT).fill(0)
}
